// src/pages/profile/Notifications.tsx
import React, { useEffect, useState } from 'react';
import {
  AlertTriangle,
  Bell,
  BellRing,
  CalendarDays,
  Check,
  CheckCheck,
  CheckCircle2,
  FileCheck2,
  Gavel,
  ShieldCheck,
  Trash2,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { id as idLocale } from 'date-fns/locale';
import { Breadcrumb, Button, Card, Select } from '../../components/ui';
import { useNotificationStore } from '../../store/notificationStore';

const getNotificationIconConfig = (icon?: string, jenis?: string) => {
  const key = String(icon || jenis || '').toLowerCase();

  if (['shield-check', 'laporan_resolved_locally'].includes(key)) {
    return {
      node: <ShieldCheck className="w-5 h-5" />,
      className: 'bg-emerald-50 text-emerald-600 border border-emerald-100',
    };
  }

  if (['file-check', 'persetujuan', 'laporan_diajukan', 'laporan_menunggu_validasi_l2'].includes(key)) {
    return {
      node: <FileCheck2 className="w-5 h-5" />,
      className: 'bg-blue-50 text-blue-600 border border-blue-100',
    };
  }

  if (['alert-triangle', 'peringatan', 'laporan_ditolak'].includes(key)) {
    return {
      node: <AlertTriangle className="w-5 h-5" />,
      className: 'bg-red-50 text-red-600 border border-red-100',
    };
  }

  if (['gavel', 'keputusan', 'ppepp_decision'].includes(key)) {
    return {
      node: <Gavel className="w-5 h-5" />,
      className: 'bg-purple-50 text-purple-600 border border-purple-100',
    };
  }

  if (['calendar-days', 'rapim', 'rapim_created'].includes(key)) {
    return {
      node: <CalendarDays className="w-5 h-5" />,
      className: 'bg-cyan-50 text-cyan-600 border border-cyan-100',
    };
  }

  if (['check-circle-2', 'sukses', 'laporan_tervalidasi_l2'].includes(key)) {
    return {
      node: <CheckCircle2 className="w-5 h-5" />,
      className: 'bg-emerald-50 text-emerald-600 border border-emerald-100',
    };
  }

  return {
    node: <BellRing className="w-5 h-5" />,
    className: 'bg-slate-50 text-slate-600 border border-slate-100',
  };
};

const NotificationsPage: React.FC = () => {
  const {
    notifications,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    fetchNotifications,
  } = useNotificationStore();
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const unreadCount = notifications.filter((notification) => !notification.read_at).length;

  const filteredNotifications = notifications.filter((notification) => {
    if (filter === 'unread') return !notification.read_at;
    if (filter === 'read') return !!notification.read_at;
    return true;
  });

  return (
    <div className="space-y-6 animate-in">
      <Breadcrumb items={[{ label: 'Notifikasi' }]} />

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-black text-teal-dark tracking-tight">Notifikasi</h1>
          <p className="text-sm text-neutral-text-secondary mt-1">
            {unreadCount > 0 ? `${unreadCount} notifikasi belum dibaca` : 'Semua notifikasi sudah dibaca'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select
            options={[
              { value: 'all', label: 'Semua' },
              { value: 'unread', label: 'Belum Dibaca' },
              { value: 'read', label: 'Sudah Dibaca' },
            ]}
            value={filter}
            onChange={setFilter}
          />
          {unreadCount > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => markAllAsRead()}
              icon={<CheckCheck className="w-4 h-4" />}
            >
              Tandai Semua Dibaca
            </Button>
          )}
        </div>
      </div>

      <div className="space-y-2">
        {filteredNotifications.map((notification) => {
          const iconConfig = getNotificationIconConfig(notification.data.icon, notification.data.jenis);

          return (
            <Card
              key={notification.id}
              hover
              padding="sm"
              className={`transition-all ${
                !notification.read_at ? 'bg-blue-50/50 border-l-4 border-l-teal-main' : ''
              }`}
            >
              <div className="flex items-start gap-4">
                <div className={`w-11 h-11 rounded-2xl shrink-0 flex items-center justify-center ${iconConfig.className}`}>
                  {iconConfig.node}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <h3
                        className={`text-sm ${
                          !notification.read_at ? 'font-bold' : 'font-medium'
                        } text-neutral-text-primary`}
                      >
                        {notification.data.title}
                      </h3>
                      <p className="text-sm text-neutral-text-secondary mt-0.5 line-clamp-2">
                        {notification.data.message}
                      </p>
                      <p className="text-xs text-neutral-text-secondary mt-1.5">
                        {formatDistanceToNow(new Date(notification.created_at), {
                          addSuffix: true,
                          locale: idLocale,
                        })}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {!notification.read_at && (
                        <button
                          onClick={() => markAsRead(notification.id)}
                          className="p-1.5 rounded-lg hover:bg-neutral-bg text-neutral-text-secondary"
                          title="Tandai dibaca"
                        >
                          <Check className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={() => deleteNotification(notification.id)}
                        className="p-1.5 rounded-lg hover:bg-danger/10 text-neutral-text-secondary hover:text-danger"
                        title="Hapus"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          );
        })}

        {filteredNotifications.length === 0 && (
          <Card className="text-center py-12">
            <Bell className="w-12 h-12 mx-auto mb-3 text-neutral-border" />
            <p className="text-lg font-bold text-neutral-text-primary">Tidak ada notifikasi</p>
            <p className="text-sm text-neutral-text-secondary mt-1">
              {filter === 'unread' ? 'Semua notifikasi sudah dibaca' : 'Belum ada notifikasi'}
            </p>
          </Card>
        )}
      </div>
    </div>
  );
};

export default NotificationsPage;
